/**
 * @file care-mcp-oauth.schema.ts
 * @description Servidor de autorização OAuth 2.1 do conector MCP de jornadas
 * (plano "Conector MCP de Jornadas", M2), conforme a especificação de
 * autorização do MCP de 2026-07-28.
 *
 * Três coleções de vida curta ou de cadastro:
 *   - `mcpOAuthClientes`: IAs registradas (registro dinâmico) ou conhecidas
 *     pelo documento de metadados de cliente (client_id = URL HTTPS).
 *   - `mcpOAuthTransacoes`: pedido de autorização validado, esperando a pessoa
 *     entrar e consentir. 10 minutos.
 *   - `mcpOAuthCodigos`: código de autorização emitido depois do consentimento.
 *     Uso único, 5 minutos, id do documento = hash do código.
 */

import { z } from 'zod';
import { EscopoDoConectorEnum } from './care-mcp-token.schema';

export const McpOAuthClienteSchema = z.object({
  clientId: z.string().min(1),
  origem: z.enum(['REGISTRO_DINAMICO', 'METADADOS']),
  clientName: z.string().min(1).max(200),
  clientUri: z.string().nullable().default(null),
  logoUri: z.string().nullable().default(null),
  redirectUris: z.array(z.string()).min(1),
  criadoEm: z.date(),
  atualizadoEm: z.date(),
});
export type McpOAuthCliente = z.infer<typeof McpOAuthClienteSchema>;

export const McpOAuthTransacaoSchema = z.object({
  id: z.string().min(16),
  clientId: z.string(),
  clientName: z.string(),
  origemDoCliente: z.enum(['REGISTRO_DINAMICO', 'METADADOS']),
  redirectUri: z.string(),
  state: z.string().nullable().default(null),
  codeChallenge: z.string(),
  escopos: z.array(EscopoDoConectorEnum).min(1),
  resource: z.string(),
  criadaEm: z.date(),
  expiraEm: z.date(),
});
export type McpOAuthTransacao = z.infer<typeof McpOAuthTransacaoSchema>;

export const McpOAuthCodigoSchema = z.object({
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  clientId: z.string(),
  clientName: z.string(),
  redirectUri: z.string(),
  codeChallenge: z.string(),
  escopos: z.array(EscopoDoConectorEnum).min(1),
  resource: z.string(),
  pessoa: z.string(),
  marca: z.string(),
  criadoEm: z.date(),
  expiraEm: z.date(),
  usadoEm: z.date().nullable().default(null),
  /** Família de tokens gerada na troca — para derrubar se o código for reusado. */
  familia: z.string().nullable().default(null),
});
export type McpOAuthCodigo = z.infer<typeof McpOAuthCodigoSchema>;
